import React, { forwardRef } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import cn from "classnames";
import { useMergeRefs } from "storefront/hooks/useMergeRefs";
import { useFormControl } from "../FormControl";
import styles from "./Checkbox.module.scss";

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name" | "type"> {
  label: string;
  rules?: RegisterOptions;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
}

/**
 * Checkbox component
 *
 * A checkbox input component that integrates with react-hook-form.
 * Includes a label that's positioned to the right of the checkbox.
 */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, rules, className, labelClassName, inputClassName, ...props },
    ref,
  ) => {
    const { register } = useFormContext();
    const { name } = useFormControl();

    const { ref: hookFormRef, ...registerProps } = register(name, rules);

    const mergedRef = useMergeRefs(hookFormRef, ref);

    return (
      <div className={cn(styles.checkboxControl, className)}>
        <input
          id={name}
          ref={mergedRef}
          type="checkbox"
          className={cn(styles.checkboxInput, inputClassName)}
          {...registerProps}
          {...props}
        />
        <label
          htmlFor={name}
          className={cn(styles.checkboxLabel, labelClassName)}
        >
          {label}
        </label>
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
